// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";
import CarouselButton, {
    CarouselButtonPresentationProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import getProductVideoUrl from "@toolkit/Framework/Common/Utilities/getProductVideoUrl";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import Video, { VideoPresentationProps } from "@store/Library/Components/Toolkit/Video/Video";
import getColor from "@insite/mobius/utilities/getColor";
import { IconWrapper } from "@insite/mobius/Icon";
import PlayLine from "@store/Library/Components/Icons/PlayLine";
import Carousel, { CarouselPresentationProps } from "@store/Overrides/Components/Carousel";
import MobiusAccordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import ManagedAccordionSection from "@store/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { AccordionSectionPanel, AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { accordionProductDetailsSectionProps } from "@store/Themes/common/props/accordionProductDetails";

type OwnProps = WidgetProps & HasProduct;

type VideoSlide = { image: ImageModel; videoUrl: string };

export interface ProductDetailsVideoCarouselStyles {
    wrapper?: InjectableCss;
    videoWrapper?: InjectableCss;
    noResult?: TypographyPresentationProps;
    carousel?: CarouselPresentationProps;
    slideWrapper?: InjectableCss;
    mediaWrapper?: InjectableCss;
    thumbClickable?: ClickablePresentationProps;
    thumbImage?: LazyImageProps;
    playButton?: CarouselButtonPresentationProps;
    video?: VideoPresentationProps;
    embedSwipeShield?: InjectableCss;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const productDetailsVideoCarouselStyles: ProductDetailsVideoCarouselStyles = {
    wrapper: {
        css: css`
            ${spacingStyles("1em", "1em")}
            width: 100%;
            margin: 0 auto;
        `,
    },
    videoWrapper: {
        css: css`
            ${spacingStyles("1em", "1em")}
            width: 100%;
            max-width: ${pxToRem(630)};
            margin: 0 auto;
        `,
    },
    noResult: {
        forwardAs: "p",
        css: css`
            margin: 0;
        `,
    },
    carousel: {
        arrowsPositionVariant: "inside",
        itemsPerSlide: 1,
        itemsToScroll: 1,
        showArrows: true,
        showDots: true,
        loop: false,
        inlineArrowsBreakpoint: "xl",
        extendedStyles: {
            carouselArrowNext: {
                extendedStyles: {
                    carouselButton: {
                        extendedStyles: {
                            button: {
                                css: css`
                                    box-shadow: none;
                                `,
                            },
                        },
                    },
                },
            },
            carouselArrowPrevious: {
                extendedStyles: {
                    carouselButton: {
                        extendedStyles: {
                            button: {
                                css: css`
                                    box-shadow: none;
                                `,
                            },
                        },
                    },
                },
            },
        },
    },
    slideWrapper: {
        css: css`
            width: 100%;
            overflow: hidden;
        `,
    },
    mediaWrapper: {
        css: css`
            position: relative;
            width: 100%;
            aspect-ratio: 16 / 9;
            overflow: hidden;
            background: rgba(0, 0, 0, 0.04);
        `,
    },
    thumbClickable: {
        css: css`
            ${focusStyles(true)}
            width: 100%;
            display: block;
            position: relative;
            cursor: pointer;
        `,
    },
    thumbImage: {
        css: css`
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;

            & img {
                width: 100%;
                height: 100%;
                object-fit: contain;
                object-position: center;
                display: block;
            }
        `,
    },
    playButton: {
        iconSize: pxToRem(20),
        iconSrc: PlayLine,
        iconMargin: "0 0 0 0.08em",
        extendedStyles: {
            wrapper: {
                css: css`
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                    background-color: #0000004d;
                `,
            },
            button: {
                css: css`
                    width: ${pxToRem(50)};
                    height: ${pxToRem(50)};
                    background: ${getColor("secondary")};

                    ${IconWrapper} svg path {
                        fill: ${getColor("secondary")};
                    }
                `,
            },
        },
    },
    video: {
        extendedStyles: {
            iframe: {
                css: css`
                    width: 100%;
                    display: block;
                    aspect-ratio: 16 / 9;
                    border: 0;
                `,
            },
            video: {
                css: css`
                    width: 100%;
                    display: block;
                    aspect-ratio: 16 / 9;
                `,
            },
        },
    },
    embedSwipeShield: {
        css: css`
            position: absolute;
            inset: 0;
            background: transparent;
            pointer-events: auto;
            touch-action: pan-y;
        `,
    },
    accordion: {
        css: css`
            margin-top: ${pxToRem(30)};
        `,
    },
    accordionSection: {
        headerProps: {
            ...accordionProductDetailsSectionProps.headerProps,
            css: css`
                ${accordionProductDetailsSectionProps.headerProps?.css}

                ${AccordionSectionPanel} + && {
                    margin-top: ${pxToRem(15)};
                }

                && button[aria-expanded="true"] {
                    border-color: ${getColor("common.border")};
                }
            `,
        },
        panelProps: {
            ...accordionProductDetailsSectionProps.panelProps,
            css: css`
                ${accordionProductDetailsSectionProps.panelProps?.css}
            `,
        },
        mergeCss: true,
    },
};

const styles = productDetailsVideoCarouselStyles;

const isEmbedUrl = (url: string) => /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
const getPoster = (image: ImageModel) => image.largeImagePath || image.mediumImagePath || image.smallImagePath;

const Poster: React.FC<{ poster?: string; altText: string; clickable?: boolean; onPlay?: () => void }> = ({
    poster,
    altText,
    clickable,
    onPlay,
}) =>
    clickable ? (
        <Clickable {...styles.thumbClickable} onClick={onPlay} aria-label={translate("Play the video")}>
            <StyledWrapper {...styles.mediaWrapper}>
                {poster ? <LazyImage {...styles.thumbImage} src={poster} altText={altText} /> : null}
                <CarouselButton
                    {...styles.playButton}
                    label={translate("Play the video")}
                    tabIndex={-1}
                    aria-hidden="true"
                />
            </StyledWrapper>
        </Clickable>
    ) : (
        <StyledWrapper {...styles.mediaWrapper}>
            {poster ? <LazyImage {...styles.thumbImage} src={poster} altText={altText} /> : null}
            <CarouselButton
                {...styles.playButton}
                label={translate("Play the video")}
                tabIndex={-1}
                aria-hidden="true"
            />
        </StyledWrapper>
    );

const VideoPlayer: React.FC<{
    url: string;
    poster?: string;
    isActiveEmbed: boolean;
    embedInteractive: boolean;
    onEnableInteraction: () => void;
}> = ({ url, poster, isActiveEmbed, embedInteractive, onEnableInteraction }) => {
    const embed = isEmbedUrl(url);

    if (embed && !isActiveEmbed) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mediaWrapper}>
            <Video
                key={embed ? `${url}-${isActiveEmbed}` : url}
                {...styles.video}
                src={url}
                isEmbededIframe={embed}
                poster={embed ? undefined : poster}
                controls
                preload="metadata"
                width="100%"
                height="100%"
            />
            {embed && isActiveEmbed && !embedInteractive ? (
                <StyledWrapper
                    {...styles.embedSwipeShield}
                    role="button"
                    aria-label={translate("Tap to interact with the video")}
                    onClick={onEnableInteraction}
                />
            ) : null}
        </StyledWrapper>
    );
};

const Slide: React.FC<{
    v: VideoSlide;
    index: number;
    activeIndex: number;
    playingIndex: number | null;
    onPlay: (index: number) => void;
    embedInteractive: boolean;
    setEmbedInteractive: (value: boolean) => void;
}> = ({ v, index, activeIndex, playingIndex, onPlay, embedInteractive, setEmbedInteractive }) => {
    const poster = getPoster(v.image);
    const altText = v.image.imageAltText || translate("Product video");
    const embed = isEmbedUrl(v.videoUrl);

    const showLocalVideo = !embed && playingIndex === index;
    const showEmbedVideo = embed && index === activeIndex;

    return (
        <StyledWrapper {...styles.slideWrapper}>
            {showLocalVideo || showEmbedVideo ? (
                <VideoPlayer
                    url={v.videoUrl}
                    poster={poster}
                    isActiveEmbed={showEmbedVideo}
                    embedInteractive={embedInteractive}
                    onEnableInteraction={() => setEmbedInteractive(true)}
                />
            ) : (
                <Poster
                    poster={poster}
                    altText={altText}
                    clickable={!embed}
                    onPlay={!embed ? () => onPlay(index) : undefined}
                />
            )}
        </StyledWrapper>
    );
};

const ProductDetailsVideoCarousel: React.FC<OwnProps> = ({ product }) => {
    const videos = React.useMemo<VideoSlide[]>(() => {
        const images = product?.images ?? [];
        return images
            .map(image => ({ image, videoUrl: getProductVideoUrl(product, image.id) }))
            .filter((x): x is VideoSlide => !!x.videoUrl);
    }, [product]);

    const [activeIndex, setActiveIndex] = React.useState(0);
    const [playingIndex, setPlayingIndex] = React.useState<number | null>(null);
    const [embedInteractive, setEmbedInteractive] = React.useState(false);

    React.useEffect(() => {
        setPlayingIndex(null);
        setEmbedInteractive(false);
    }, [activeIndex]);

    if (videos.length === 0) {
        return null;
    }

    const render = (v: VideoSlide, index: number) => (
        <Slide
            v={v}
            index={index}
            activeIndex={activeIndex}
            playingIndex={playingIndex}
            onPlay={setPlayingIndex}
            embedInteractive={embedInteractive}
            setEmbedInteractive={setEmbedInteractive}
        />
    );

    if (videos.length === 1) {
        return (
            <MobiusAccordion headingLevel={1} {...styles.accordion}>
                <ManagedAccordionSection
                    initialExpanded={true}
                    title={translate("Videos")}
                    {...styles.accordionSection}
                >
                    <StyledWrapper {...styles.videoWrapper}>{render(videos[0], 0)}</StyledWrapper>
                </ManagedAccordionSection>
            </MobiusAccordion>
        );
    }

    return (
        <MobiusAccordion headingLevel={1} {...styles.accordion}>
            <ManagedAccordionSection initialExpanded={true} title={translate("Documents")} {...styles.accordionSection}>
                <StyledWrapper {...styles.videoWrapper}>
                    <Carousel
                        {...styles.carousel}
                        id={`product-video-carousel-${product?.id ?? "unknown"}`}
                        slides={videos.map((v, index) => ({ id: v.image.id, renderComponent: () => render(v, index) }))}
                        onSelectIndex={setActiveIndex}
                    />
                </StyledWrapper>
            </ManagedAccordionSection>
        </MobiusAccordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsVideoCarousel),
    definition: {
        displayName: "Video Carousel",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
